import { api } from "../lib";
import type {
  ClientReportIdentity,
  ReportIdentitySource,
} from "../utils/reportIdentity";

export type { ReportIdentitySource };

// ─── Prefix Types ─────────────────────────────────────────────────────────────

export interface PrefixTypeDto {
  id: string;
  name: string;
  assignmentStrategy:
    | "requisition_signal"
    | "lab_identity"
    | "doctor_identity"
    | "doctor_practice_insurance"
    | "doctor_practice_fixed"
    | "fallback";
  priority: number;
}

export async function getPrefixTypes(): Promise<PrefixTypeDto[]> {
  const response = await api.get<PrefixTypeDto[]>("/prefix-types");
  return response;
}

// ─── Lookup ───────────────────────────────────────────────────────────────────

export interface PrefixLookupResult {
  prefixId: string;
  prefixCode: string;
  prefixColor: string | null;
  prefixType: {
    name: string;
    assignmentStrategy: string;
    priority: number;
  } | null;
  billingPending: boolean;
  fallbackPrefixCode: string | null;
  resolvedBillingScenarioId: string | null;
  resolvedBillingScenarioName: string | null;
  allowedBillingScenarios: { id: string; name: string; isDefault: boolean }[];
}

/**
 * Resolve the case prefix at accessioning based on practice + signal flags.
 * Uses the 5-step hierarchy: signals → lab identity → insurance gated → fixed → fallback.
 */
export async function lookupPrefix(params: {
  practiceId?: string;
  physicianId?: string;
  surgeryCenterLabId?: string;
  insuranceProviderId?: string;
  insurancePlanTypeId?: string;
  isCytology?: boolean;
  isResearch?: boolean;
  isConsultation?: boolean;
  isTcOnly?: boolean;
}): Promise<PrefixLookupResult> {
  return api.post<PrefixLookupResult>("/prefixes/lookup", {
    practiceId: params.practiceId || null,
    physicianId: params.physicianId || null,
    surgeryCenterLabId: params.surgeryCenterLabId || null,
    insuranceProviderId: params.insuranceProviderId || null,
    insurancePlanTypeId: params.insurancePlanTypeId || null,
    isCytology: params.isCytology ?? false,
    isResearch: params.isResearch ?? false,
    isConsultation: params.isConsultation ?? false,
    isTcOnly: params.isTcOnly ?? false,
  });
}

// ─── Admin Types ──────────────────────────────────────────────────────────────

export interface AllowedBillingScenario {
  id: string;
  billingScenarioId: string;
  billingScenario?: { id: string; name: string } | null;
  isDefault: boolean;
}

export interface AllowedBillingScenarioRequest {
  billingScenarioId: string;
  isDefault: boolean;
}

export interface PrefixMapping {
  id: string;
  prefixId: string;
  practiceId: string | null;
  practiceName: string | null;
  surgeryCenterLabId: string | null;
  surgeryCenterLabName: string | null;
  physicianId: string | null;
  physicianFirstName: string | null;
  physicianLastName: string | null;
  priority: number;
}

export interface MappingRequest {
  practiceId?: string | null;
  surgeryCenterLabId?: string | null;
  physicianId?: string | null;
  priority?: number;
}

export interface EligibilityRule {
  id: string;
  prefixId: string;
  insuranceProviderId: string | null;
  insuranceProviderName: string | null;
  insuranceProvider: { id: string; name: string } | null;
  isCatchAll: boolean;
  tcpcEligible: boolean;
}

export interface EligibilityRuleRequest {
  insuranceProviderId?: string | null;
  isCatchAll?: boolean;
  tcpcEligible: boolean;
}

export interface PrefixSummary {
  id: string;
  code: string;
  prefixTypeId: string | null;
  prefixType?: PrefixTypeDto | null;
  color: string | null;
  fallbackPrefixId: string | null;
  isActive: boolean;
  reportIdentitySource: ReportIdentitySource;
}

export interface PrefixDetail extends PrefixSummary {
  mappings: PrefixMapping[];
  allowedBillingScenarios: AllowedBillingScenario[];
  eligibilityRules: EligibilityRule[];
  fallbackPrefix?: { id: string; code: string } | null;
  sequence?: {
    prefixCode: string;
    year: number;
    lastIssued: number;
    nextSequence: number;
    nextCaseNumber: string;
  };
}

export interface PrefixListResponse {
  data: PrefixSummary[];
  total: number;
  page: number;
  limit: number;
}

export interface CreatePrefixRequest {
  code: string;
  prefixTypeId: string;
  reportIdentitySource?: ReportIdentitySource;
  color?: string | null;
  fallbackPrefixId?: string | null;
  allowedBillingScenarios?: AllowedBillingScenarioRequest[];
}

export interface UpdatePrefixRequest {
  prefixTypeId?: string;
  reportIdentitySource?: ReportIdentitySource;
  color?: string | null;
  fallbackPrefixId?: string | null;
  isActive?: boolean;
  allowedBillingScenarios?: AllowedBillingScenarioRequest[];
  nextSequence?: number;
  sequenceChangeReason?: string;
}

export interface GlobalEligibilityRule {
  insuranceProviderId: string;
  allowSplitBilling: boolean;
}

export interface ReportIdentityPreview {
  mappingId: string | null;
  targetId: string | null;
  targetName: string;
  logoS3Key: string | null;
  identity: ClientReportIdentity;
  warnings: string[];
  isComplete: boolean;
}

export interface ReportIdentityPreviewResponse {
  source: ReportIdentitySource;
  previews: ReportIdentityPreview[];
  warnings: string[];
}

// ─── Admin API Methods ────────────────────────────────────────────────────────

export async function listPrefixes(options?: {
  includeInactive?: boolean;
  page?: number;
  limit?: number;
  all?: boolean;
}): Promise<PrefixListResponse> {
  const params = new URLSearchParams();
  if (options?.all === true) {
    params.set("all", "true");
    if (options?.includeInactive) params.set("includeInactive", "true");
  } else {
    if (options?.includeInactive) params.set("includeInactive", "true");
    if (options?.page) params.set("page", String(options.page));
    if (options?.limit) params.set("limit", String(options.limit));
  }
  const query = params.toString();
  return api.get<PrefixListResponse>(`/prefixes${query ? `?${query}` : ""}`);
}

export async function getPrefixById(
  id: string,
  options?: { includeSequence?: boolean }
): Promise<PrefixDetail> {
  const query = options?.includeSequence ? "?includeSequence=true" : "";
  return api.get<PrefixDetail>(`/prefixes/${id}${query}`);
}

export async function createPrefix(
  dto: CreatePrefixRequest
): Promise<PrefixDetail> {
  return api.post<PrefixDetail>("/prefixes", dto);
}

export async function updatePrefix(
  id: string,
  dto: UpdatePrefixRequest
): Promise<PrefixDetail> {
  return api.put<PrefixDetail>(`/prefixes/${id}`, dto);
}

export async function getReportIdentityPreview(
  prefixId: string,
  source: ReportIdentitySource
): Promise<ReportIdentityPreviewResponse> {
  return api.get<ReportIdentityPreviewResponse>(
    `/prefixes/${prefixId}/report-identity-preview?source=${encodeURIComponent(
      source
    )}`
  );
}

export async function deactivatePrefix(id: string): Promise<void> {
  await api.delete<void>(`/prefixes/${id}`);
}

export async function addMapping(
  prefixId: string,
  dto: MappingRequest
): Promise<PrefixMapping> {
  return api.post<PrefixMapping>(`/prefixes/${prefixId}/mappings`, dto);
}

export async function updateMappingPriority(
  prefixId: string,
  mappingId: string,
  priority: number
): Promise<PrefixMapping> {
  return api.put<PrefixMapping>(`/prefixes/${prefixId}/mappings/${mappingId}`, {
    priority,
  });
}

export async function removeMapping(
  prefixId: string,
  mappingId: string
): Promise<void> {
  await api.delete<void>(`/prefixes/${prefixId}/mappings/${mappingId}`);
}

export async function getEligibilityRules(
  prefixId: string
): Promise<EligibilityRule[]> {
  return api.get<EligibilityRule[]>(`/prefixes/${prefixId}/eligibility-rules`);
}

export async function replaceEligibilityRules(
  prefixId: string,
  rules: EligibilityRuleRequest[]
): Promise<EligibilityRule[]> {
  return api.put<EligibilityRule[]>(`/prefixes/${prefixId}/eligibility-rules`, {
    rules,
  });
}

export async function updateGlobalEligibility(
  rules: GlobalEligibilityRule[]
): Promise<void> {
  await api.put<void>("/admin/insurance-eligibility/global", { rules });
}
